import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { AuthShell, Field } from "./Login";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title="Check your email" subtitle="We've notified our support team.">
        <div
          data-testid="forgot-success"
          className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3"
        >
          <CheckCircle2 size={22} className="text-emerald-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700 leading-relaxed">
            If an account exists for <b>{email}</b>, you'll receive a password reset email shortly with a temporary
            password. Sign in with it, then change your password from{" "}
            <span className="font-semibold text-[var(--cavilo-ink)]">Settings → Change password</span>.
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between">
          <button
            data-testid="forgot-back-login"
            onClick={() => nav("/login")}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--cavilo-blue)] hover:underline"
          >
            <ArrowLeft size={14} /> Back to sign in
          </button>
          <Link
            to="/contact"
            className="text-xs text-slate-500 hover:text-[var(--cavilo-ink)]"
          >
            Need help?
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot your password?"
      subtitle="Enter your account email and we'll send you a reset password by email."
    >
      <form onSubmit={submit} className="space-y-4">
        <Field icon={Mail} label="Email">
          <input
            data-testid="forgot-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent outline-none text-sm placeholder:text-slate-400"
            placeholder="you@work.com"
          />
        </Field>
        <button
          data-testid="forgot-submit"
          type="submit"
          disabled={busy}
          className="w-full rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white py-3 font-bold text-sm shadow-lg shadow-blue-500/20 transition-colors disabled:opacity-60 btn-press"
        >
          {busy ? "Sending request…" : "Send reset password email"}
        </button>
      </form>

      <p className="text-xs text-slate-500 mt-4 leading-relaxed">
        Our team will email you a temporary password. After signing in, please change it from
        Settings → Change password.
      </p>

      <p className="text-sm text-slate-500 text-center mt-6">
        Remembered it?{" "}
        <Link
          to="/login"
          data-testid="forgot-to-login"
          className="font-semibold text-[var(--cavilo-blue)] hover:underline"
        >
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
